import KeyCloak, { KeycloakInitOptions, KeycloakInstance } from 'keycloak-js'
import JSONAPISource from '@orbit/jsonapi'
import { KeyMap, Schema, Transform } from '@orbit/data'
import { timeout } from '@/utils/time'
import { CustomJSONAPISerializer } from '@/http/CustomJSONAPISerializer'
import { CSV_REPORT_URL_BASE } from '@/constants/urls'
import moment from 'moment-timezone'
// VUE_APP_KEY_CLOAK_REALM=dashboard
// VUE_APP_KEY_CLOAK_AUTH_SERVER_URL=https://auth.agartee.ca/auth
// VUE_APP_KEY_CLOAK_SSL_REQUIRED=external
// VUE_APP_KEY_CLOAK_RESOURCE=dash-ui
// VUE_APP_KEY_CLOAK_PUBLIC_CLIENT=true

function keyCloakConfigValue(key: string): string {
  const configKey = `VUE_APP_KEY_CLOAK_${key}`
  return process.env[configKey] || ''
}

class KCHolder {
  public static get Instance(): KCHolder {
    // Do you need arguments? Make it a regular method instead.
    return this._instance || (this._instance = new this())
  }

  private static _instance: KCHolder
  private kc: KeycloakInstance

  private constructor() {
    console.log('Constructor of KC')
    this.kc = new KeyCloak({
      realm: keyCloakConfigValue('REALM'),
      url: keyCloakConfigValue('AUTH_SERVER_URL'),
      // 'ssl-required': keyCloakConfigValue('SSL_REQUIRED'),
      clientId: keyCloakConfigValue('RESOURCE'),
      // 'public-client': JSON.parse(keyCloakConfigValue('PUBLIC_CLIENT')),
    })
    // TODO temporarily code
    this.kc.onAuthError = errorData => {
      console.warn({
        type: 'onAuthError',
        errorData,
      })
    }
    this.kc.onAuthRefreshError = async () => {
      console.warn({
        type: 'onAuthRefreshError',
      })
      console.warn({
        user: await this.getUser(),
        tokenParsed: this.kc && this.kc.tokenParsed,
      })
    }
    this.initKeyCloak().then(() => {
      console.log('Done init cloak')
    })
  }

  public async getJsonApiRemoteResource(schema: Schema, options: any = {}): Promise<JSONAPISource> {
    console.log(`Getting json api remote resource`, schema.models, options)
    const serverUrl = process.env.VUE_APP_API_SERVER_URL
    const apiBasePath = process.env.VUE_APP_JSON_API_BASE_PATH
    const token = await this.getToken()
    // console.log('Token for API call: ', token)
    return new JSONAPISource({
      schema: schema,
      name: 'remote',
      host: `${serverUrl}/${apiBasePath}`,
      defaultFetchSettings: {
        timeout: 60000,
        headers: {
          Accept: `application/json, application/vnd.api+json`,
          Authorization: `Bearer ${token || 'notset'}`,
          'cache-control': `no-cache`,
        },
      },
      ...options,
    })
  }

  public async getJsonApiRemoteResourceForNewRecord(schema: Schema): Promise<JSONAPISource> {
    const keyMap = new KeyMap()
    return this.getJsonApiRemoteResource(schema, {
      keyMap: keyMap,
      SerializerClass: CustomJSONAPISerializer,
    })
  }

  public async getToken(): Promise<string> {
    await this.waitUntilTokenReady()
    await new Promise((resolve, reject) => {
      this.kc
        .updateToken(5)
        .then(updated => {
          resolve()
        })
        ?.catch(e => {
          reject()
        })
    })
    return this.kc.token
  }

  public async getUser(): Promise<string> {
    return await this.getAttribute('preferred_username')
  }

  public async getUserFirstName(): Promise<string> {
    return await this.getAttribute('given_name')
  }

  public async getUserLastName(): Promise<string> {
    return await this.getAttribute('family_name')
  }

  public async getRoles(): Promise<string[]> {
    const resourceAccess = await this.getAttribute('resource_access')
    const resourceClientApi = process.env.VUE_APP_KEY_CLOAK_CLIENT_API
    const realmAccessObj = resourceAccess && resourceAccess[resourceClientApi]
    const roles = realmAccessObj && realmAccessObj['roles']
    return roles
  }

  public async getPdfFetchConfig(): Promise<RequestInit> {
    console.log('Before getting token and user')
    const token = await this.getToken()
    console.log('After getting token and user')
    return {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/pdf',
        Authorization: `Bearer ${token}`,
      },
    } as RequestInit
  }

  public async getCsvFetchConfig(): Promise<RequestInit> {
    console.log('Before getting token and user')
    const token = await this.getToken()
    console.log('After getting token and user')
    return {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'text/csv',
        Authorization: `Bearer ${token}`,
      },
    } as RequestInit
  }

  private async getAttribute(key: string): Promise<string> {
    await this.waitUntilTokenReady()
    const tokenParsed = this.kc && this.kc.tokenParsed
    // console.log('token parsed: ')
    // console.log(tokenParsed)
    return (this.kc && this.kc.tokenParsed && this.kc.tokenParsed[key]) || ''
  }

  private async waitUntilTokenReady(): Promise<void> {
    while (!this.kc || !this.kc.token) {
      await timeout(2000)
    }
  }

  private async initKeyCloak(): Promise<void> {
    const initOptions = {
      type: 'hybrid',
      responseMode: 'fragment',
      flow: 'standard',
      onLoad: 'login-required',
      checkLoginIframe: process.env.NODE_ENV === 'production', // this line
    } as KeycloakInitOptions
    const publicClientSt = keyCloakConfigValue('PUBLIC_CLIENT')
    console.log(`Public client is ${publicClientSt}`)
    await this.kc.init(initOptions)
  }
}

export const KC = KCHolder.Instance

export function findRemoteIdFromNewRecord(transforms: Transform[]): string {
  // Originally this is Transform type
  const transform: any = transforms[1]
  if (!transform) {
    return ''
  }

  // Originally operation is Operation type, not any
  const operation: any = transform.operations.find(op => op.key === 'remoteId')

  if (!operation) {
    return ''
  }

  return operation.value || ''
}

export function getCsvDownloadFunction({ path = 'events' }): () => Promise<void> {
  const csvUrl = `${CSV_REPORT_URL_BASE}/${path}`
  return async function(): Promise<void> {
    console.log(`fetching csv from ${csvUrl}`)
    const config = await KC.getCsvFetchConfig()
    const res = await fetch(csvUrl, config)
    if (res.status !== 200) {
      return
    }
    const data = await res.blob()
    const blob = new Blob([data], { type: 'text/csv' })
    const filePath = URL.createObjectURL(blob)
    const filenameWithExt = `agartee_${path.replace(/\s/g, '_')}_generated_${moment().format()}.csv`

    if (navigator.msSaveOrOpenBlob) {
      navigator.msSaveOrOpenBlob(blob, filenameWithExt)
    } else {
      let a = document.createElement('a')
      a.setAttribute('href', filePath)
      a.setAttribute('download', filenameWithExt)
      a.classList.add('d-none')
      a.click()
      a = null
    }
  }
}
